import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { BoostMark } from '../../components/ui/BoostMark';
import { copy } from '../../config/copy';
import { Button, LinkButton } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { Artwork } from '../dashboard/Artwork';
import type { TrackSummary } from '../tracks/TrackList';
import { FindTracks, type FoundTrack } from './FindTracks';
import { duration, rooms, type Room } from './api';
import { Textarea } from '../../components/ui/form';

/**
 * Putting one of your tracks in a host's queue.
 *
 * ## Two steps, because they are two decisions
 *
 * Which track, then what to say about it. Collapsing them into one screen means
 * a note box sitting empty above a list somebody has not chosen from yet, and a
 * submit button that is disabled for a reason two hundred pixels away.
 *
 * Going back is free and keeps the note, because "actually, the other one"
 * happens constantly and losing a sentence somebody just wrote to it would be
 * the memorable part of the interaction.
 *
 * ## Only your own tracks
 *
 * The API accepts anybody's - that is how a room finds things, and the design's
 * queue is full of listeners submitting other people's records. This dialog is
 * the narrow case that needs no search: an artist putting their own work in
 * front of a host. Submitting somebody else's needs a catalogue search, which
 * is its own component and is not this one.
 */
export function SubmitDialog({
  room,
  username,
  onClose,
  onSubmitted,
}: {
  room: Room;
  /** Whose tracks to offer - the person doing the submitting. */
  username: string;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [chosen, setChosen] = useState<FoundTrack | null>(null);
  /*
   * Which half of submission this is.
   *
   * Your own catalogue is the common case and stays the default. The other
   * tab is the one the earned boost is built on, and it had no screen at all
   * until now - see `FindTracks`.
   */
  const [mode, setMode] = useState<'mine' | 'find'>('mine');
  const [note, setNote] = useState('');
  /**
   * How this submission should go in.
   *
   * One choice rather than a toggle plus a dialog: `free` is the default,
   * `earned` spends one they already hold, and the two paid tiers open a
   * checkout after the track is queued.
   */
  const [tier, setTier] = useState<'free' | 'earned' | 'basic' | 'priority'>('free');

  /**
   * Whether the track made it into the queue before something went wrong.
   *
   * Only ever true when the checkout half failed, and it changes what the error
   * says - see `onError`.
   */
  const [queuedAnyway, setQueuedAnyway] = useState(false);
  const queryClient = useQueryClient();

  /*
   * What they have earned. Fetched here rather than carried on the session,
   * because it moves while somebody is sitting on the site - a host elsewhere
   * playing a track they submitted is what changes it.
   */
  const { data: boosts } = useQuery({
    queryKey: ['my-boosts'],
    queryFn: () => rooms.boosts(),
  });

  const { data, isPending } = useQuery({
    queryKey: ['tracks', username],
    queryFn: () => api.get<{ items: TrackSummary[] }>(`/users/${username}/tracks`),
  });

  /*
   * Queued first, paid second - in one press.
   *
   * The choice belongs here, next to the earned boost, because "how does this
   * go in" is one question and answering half of it in a second dialog made
   * people find the offer again on a strip they had to scroll to.
   *
   * The *order* is what keeps that safe, not the separation. A room can refuse
   * a submission - closed, followers only, no explicit tracks - and it refuses
   * before any money is asked for. If the checkout is then abandoned or
   * declined, the track is already in the queue at normal weight and there is
   * nothing to unpick.
   */
  const submit = useMutation({
    mutationFn: async () => {
      const paid = tier === 'basic' || tier === 'priority';

      const queued = await rooms.submit(
        room.username,
        chosen!.id,
        tier === 'earned' ? 'earned' : 'free',
        note.trim() || null,
      );

      // Recorded before the second request, because it is the fact the error
      // message needs and the second request is the one that can fail.
      if (paid) setQueuedAnyway(true);

      if (!paid) {
        return { queued, checkout: null };
      }

      const checkout = await api.post<{ checkout_url: string }>(
        `/users/${room.username}/live/queue/${queued.id}/boost`,
        { tier },
      );

      return { queued, checkout };
    },
    onSuccess: (result) => {
      /*
       * Spending one changes a number on screens this dialog cannot see - the
       * account menu carries it on every page, and the dashboard card carries
       * it too. Both read `my-boosts`, so one invalidation is the whole fix.
       *
       * Only when a boost was actually spent: a free submission changes no
       * balance, and refetching to confirm that would be a request per
       * submission that can only ever return what it already had.
       */
      if (tier === 'earned') {
        void queryClient.invalidateQueries({ queryKey: ['my-boosts'] });
      }

      if (result.checkout) {
        /* A full navigation, not a new tab. See `TipButton`. */
        window.location.href = result.checkout.checkout_url;

        return;
      }

      onSubmitted();
    },
    onError: () => {
      /*
       * Which half failed decides what to say.
       *
       * If the submission itself was refused, nothing happened. If the checkout
       * was, the track is queued and only the boost did not start - and telling
       * somebody "that could not be sent" about a track that is sitting in the
       * queue is the kind of wrong that gets submitted twice.
       */
      void queryClient.invalidateQueries({ queryKey: ['room', room.username] });
    },
  });

  /*
   * Only what a room can actually play. A track still encoding, or one the
   * artist kept private, would be refused by the server - offering it and then
   * failing is a worse way to explain the rule than not offering it.
   */
  const playable = (data?.items ?? []).filter(
    (track) => track.status === 'ready' && track.visibility === 'public',
  );

  return (
    <Modal
      title={chosen ? 'Anything for the host?' : `Submit to ${room.host.display_name}`}
      onClose={onClose}
    >
      {chosen ? (
        <div className="flex flex-col gap-5" data-submit-note>
          <Card className="flex items-center gap-3 p-3">
            <Artwork
              track={{ title: chosen.title, artwork: null, artwork_small: chosen.artwork_small ?? null }}
              className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-ink">{chosen.title}</span>
              {/* Whose track this is, once it can be somebody else's. Confirming
                  a submission without saying whose music it is invites sending
                  the wrong one. */}
              <span className="truncate font-mono text-[11px] text-faint">
                {chosen.owner ? (chosen.owner.display_name ?? chosen.owner.username) + ' · ' : ''}
                {duration(chosen.duration_ms)}
              </span>
            </div>
            <button
              type="button"
              data-change-track
              onClick={() => setChosen(null)}
              className="shrink-0 text-xs text-soft transition-colors hover:text-ink"
            >
              Change
            </button>
          </Card>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="submit-note" className="text-sm font-medium text-soft">
              A note, just for them
            </label>
            <Textarea
              id="submit-note"
              value={note}
              maxLength={500}
              autoFocus
              data-note
              placeholder="First upload, the drop is at 1:40, no pressure — whatever helps them place it."
              onChange={(event) => setNote(event.target.value)}
              className="min-h-24 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
            />
            {/* Said plainly, because the whole value of the field is that it is
                not the public chat - and somebody who assumes otherwise will
                write something different. */}
            <p className="text-xs text-faint">
              Only {room.host.display_name} sees this. It is not shown in the room or the queue.
            </p>
          </div>

          {/*
            How it goes in, as one choice.

            The earned boost used to be a lone checkbox here and the paid tiers
            were a separate dialog after submitting - which split one question
            across two screens and buried the paid half. They are alternatives,
            so they are radios: picking one unpicks the others, and `free` is
            the default because a submission that costs nothing is the normal
            case.

            Paid tiers appear only when the room can actually take money - see
            `room.boosts`, which the server nulls when the feature is off or the
            host cannot be paid.
          */}
          <fieldset
            /* The children carry `role="radio"`, which means nothing without a
               group around them - a fieldset is not one to a screen reader. */
            role="radiogroup"
            aria-label="How it goes in"
            className="flex flex-col gap-2"
            data-submit-tier
          >
            <legend className="mb-1 text-sm font-medium text-soft">How it goes in</legend>

            <TierOption
              id="free"
              selected={tier === 'free'}
              onSelect={() => setTier('free')}
              title="Normal"
              detail="Joins the queue and comes up in its turn."
            />

            {/*
              Offered only when they have one, and never as a default. It is
              spent, it does not come back, and an option that started selected
              would spend it for people who never decided to.
            */}
            {(boosts?.available ?? 0) > 0 && (
              <TierOption
                id="earned"
                selected={tier === 'earned'}
                onSelect={() => setTier('earned')}
                tone="fresh"
                title={`Spend a ${copy['noun.boost.earned']}`}
                detail={`Goes in above every paid boost — only a host pick outranks it. You have ${
                  boosts?.available
                }, and spending one does not get it back.`}
              />
            )}

            {room.boosts && (
              <>
                <TierOption
                  id="basic"
                  selected={tier === 'basic'}
                  onSelect={() => setTier('basic')}
                  title="Boost"
                  price={room.boosts.basic_cents}
                  detail="3× weight in how the queue sorts."
                />
                <TierOption
                  id="priority"
                  selected={tier === 'priority'}
                  onSelect={() => setTier('priority')}
                  title="Priority boost"
                  price={room.boosts.priority_cents}
                  detail="8× weight. The most money can buy here."
                />
              </>
            )}

            {/* Said quietly, because it is the state almost everybody is in and
                it should read as something to earn rather than something
                denied. */}
            {(boosts?.available ?? 0) === 0 && boosts !== undefined && (
              <p className="font-mono text-[10px] leading-relaxed text-ghost">
                {copy['noun.boost.earned'].toLowerCase()} · {boosts.toward_next}/{boosts.threshold} —
                earned when hosts play the tracks you send them
              </p>
            )}

            {(tier === 'basic' || tier === 'priority') && (
              <p className="text-xs text-faint">
                You will be taken to Stripe after the track is queued. If you
                change your mind there, it stays in the queue at normal weight.
              </p>
            )}
          </fieldset>

          {submit.isError && (
            <Alert tone="error">
              {queuedAnyway
                ? /*
                   * The submission worked and the checkout did not, which is a
                   * different sentence. Somebody told "that could not be sent"
                   * about a track already sitting in the queue submits it again.
                   */
                  `${chosen.title} is in the queue at normal weight — the boost could not be started. You can try again from the queue strip.`
                : submit.error instanceof ApiError
                  ? submit.error.message
                  : 'That could not be sent.'}
            </Alert>
          )}

          <div className="flex items-center gap-3">
            <Button loading={submit.isPending} onClick={() => submit.mutate()} data-confirm-submit>
              Put it in the queue
            </Button>
            <Button variant="ghost" onClick={() => setChosen(null)} disabled={submit.isPending}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-submit-pick>
          <p className="text-sm text-soft">
            {room.status === 'live'
              ? 'It joins the queue and plays when it comes up.'
              : `${room.host.display_name} is off air. It will be waiting when they open.`}
          </p>

          {/*
            Two sources for one slot. Tabs rather than one merged list: "my
            tracks" is a short list somebody scans and "everything else" is a
            search, and a control that is both at once is neither.
          */}
          <div className="flex items-center gap-1 self-start rounded-[var(--radius-pill)] border border-line p-1" role="tablist" data-submit-source>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'mine'}
              data-source="mine"
              onClick={() => setMode('mine')}
              className={'rounded-[var(--radius-pill)] px-3 py-1 font-mono text-[11px] transition-colors ' + (mode === 'mine' ? 'bg-accent text-accent-ink' : 'text-soft hover:bg-raised hover:text-ink')}
            >
              Your tracks
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'find'}
              data-source="find"
              onClick={() => setMode('find')}
              className={'rounded-[var(--radius-pill)] px-3 py-1 font-mono text-[11px] transition-colors ' + (mode === 'find' ? 'bg-accent text-accent-ink' : 'text-soft hover:bg-raised hover:text-ink')}
            >
              Someone else's
            </button>
          </div>

          {mode === 'find' ? (
            <FindTracks onPick={setChosen} />
          ) : isPending ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : playable.length === 0 ? (
            <Card className="flex flex-col items-start gap-3 p-5">
              <p className="text-sm text-soft">
                {(data?.items.length ?? 0) === 0
                  ? 'You have not released anything yet.'
                  : 'Nothing of yours is public and finished processing yet.'}
              </p>
              <LinkButton to="/upload" variant="secondary">
                Drop a track
              </LinkButton>
            </Card>
          ) : (
            <ul className="flex max-h-[22rem] flex-col gap-2 overflow-y-auto" data-my-tracks>
              {playable.map((track) => (
                <li key={track.id}>
                  <button
                    type="button"
                    data-pick-track={track.id}
                    onClick={() => setChosen(track)}
                    className="flex w-full items-center gap-3 rounded-[var(--radius-card)] border border-line p-3 text-left transition-colors hover:border-line-strong hover:bg-raised"
                  >
                    <Artwork
                      track={{
                        title: track.title,
                        artwork: null,
                        artwork_small: track.artwork_small ?? null,
                      }}
                      className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-ink">{track.title}</span>
                      <span className="font-mono text-[11px] text-faint">
                        {duration(track.duration_ms)} · {track.play_count.toLocaleString()} plays
                      </span>
                    </span>
                    <span aria-hidden className="shrink-0 text-faint">
                      ›
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-faint">
            Submitting somebody else's track?{' '}
            <Link to="/search" className="text-accent hover:underline">
              Find it in search
            </Link>{' '}
            — a picker here is coming.
          </p>
        </div>
      )}
    </Modal>
  );
}

/**
 * One way a submission can go in.
 *
 * A button rather than an `<input type="radio">` with a label, because the whole
 * card is the target and the design's other pickable cards work the same way.
 * `role="radio"` and `aria-checked` are what make that a real choice to anybody
 * not using a mouse.
 */
function TierOption({
  id,
  selected,
  onSelect,
  title,
  detail,
  price,
  tone = 'accent',
}: {
  id: string;
  selected: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
  /** In cents, for the paid tiers. Absent means it costs nothing. */
  price?: number;
  /** `fresh` marks the earned boost, which is not bought and should not look it. */
  tone?: 'accent' | 'fresh';
}) {
  const edge = tone === 'fresh' ? 'border-fresh-edge bg-fresh-wash' : 'border-accent bg-raised';
  const dot = tone === 'fresh' ? 'border-transparent bg-fresh' : 'border-transparent bg-accent';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-submit-tier={id}
      onClick={onSelect}
      className={`flex items-start gap-3 rounded-[var(--radius-card)] border p-3.5 text-left transition-colors ${
        selected ? edge : 'border-line hover:border-line-strong'
      }`}
    >
      <span
        aria-hidden
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          selected ? dot : 'border-line-strong'
        }`}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
            {/* Marked, because this is the one row in a list of prices that is
                not a price, and the eye needs telling before it reads. */}
            {tone === 'fresh' && <BoostMark className="h-3.5 w-3.5 shrink-0 text-fresh" />}
            {title}
          </span>
          {price !== undefined && (
            <span className="shrink-0 font-mono text-[12px] text-soft">
              ${(price / 100).toFixed(2)}
            </span>
          )}
        </span>
        <span className="text-xs leading-relaxed text-soft">{detail}</span>
      </span>
    </button>
  );
}
